import { useMemo } from 'react'
import { X } from 'lucide-react'
import { useChatLayout } from '../../../context/ChatLayoutContext.jsx'
import { useIntelligentWeb } from '../../../context/IntelligentWebContext.jsx'
import {
  combineSuggestions,
  resolveStripDynamicSuggestions,
} from '../../../lib/chatUtils.js'
import { useFrozenWhileStreaming } from '../../../hooks/useFrozenWhileStreaming.js'
import { useForm } from '../../../context/FormContext.jsx'
import { useOrderStatus } from '../../../context/OrderStatusContext.jsx'
import { SuggestionsStrip } from '../SuggestionsStrip.jsx'
import { LeadFormInPanel } from '../FormOverlay.jsx'
import { OrderStatusInPanel } from '../OrderStatusPanel.jsx'
import { cn } from '../../../lib/cn.js'

/**
 * Desktop overlay shell matching v1 `OverlayChatLayout`: wide dialog,
 * chat on the left, suggestions carousel on the right; on narrow viewports
 * the same two regions stack (50 / 50). Includes the floating close control
 * from v1 (brand-colored circle, white ring).
 */
export function OverlaySplitSurface({
  conf,
  messages = [],
  onClose,
  onSend,
  ochatbot,
  onLeadFormSubmit,
  onOrderStatusResult,
  children,
}) {
  const { suggestions } = useChatLayout()
  const { activeCtas, activeLeadForms } = useIntelligentWeb()
  const { isFormOpen, formColumn } = useForm()
  const { isOrderStatusOpen, orderStatusColumn } = useOrderStatus()
  const isStreaming = useMemo(
    () => messages.some((m) => m?.streaming),
    [messages],
  )
  const liveDynamic = useMemo(
    () =>
      resolveStripDynamicSuggestions(messages, suggestions, conf, activeLeadForms),
    [messages, suggestions, conf, activeLeadForms],
  )
  const dynamicSuggestions = useFrozenWhileStreaming(liveDynamic, isStreaming)
  const combined = useMemo(
    () =>
      combineSuggestions(dynamicSuggestions, activeCtas, activeLeadForms, conf),
    [dynamicSuggestions, activeCtas, activeLeadForms, conf],
  )
  const hasSide = combined.length > 0

  const btnBg = conf?.widgetButtonColor?.trim?.() || '#0d870d'
  const btnFg = conf?.widgetButtonTextColor?.trim?.() || '#ffffff'

  return (
    <div className="relative flex h-full min-h-0 w-full flex-col overflow-hidden rounded-[inherit] bg-[var(--color-surface)]">
      <button
        type="button"
        onClick={onClose}
        aria-label="Close chat"
        className={cn(
          'absolute right-4 top-4 z-[30] flex h-8 w-8 items-center justify-center rounded-full',
          'border-2 border-white shadow-md transition-shadow hover:shadow-lg',
        )}
        style={{ backgroundColor: btnBg, color: btnFg }}
      >
        <X className="h-5 w-5 shrink-0" strokeWidth={2.5} />
      </button>

      <div
        className={cn(
          'grid min-h-0 flex-1 grid-rows-[minmax(0,1fr)_minmax(0,1fr)]',
          'md:grid-cols-2 md:grid-rows-1',
        )}
      >
        <div
          className={cn(
            'flex min-h-0 flex-col overflow-hidden border-[var(--color-border)]',
            'border-b max-md:pt-10 md:border-b-0 md:border-r md:pt-0',
            'md:rounded-tl-2xl',
          )}
        >
          {children}
        </div>
        <div
          className={cn(
            'relative flex min-h-0 flex-col overflow-hidden',
            'bg-[var(--color-muted)]',
            'p-2 md:p-3',
            'md:rounded-tr-2xl',
          )}
        >
          {hasSide ? (
            <SuggestionsStrip
              suggestions={dynamicSuggestions}
              conf={conf}
              variant="side"
              onSend={onSend}
              ochatbot={ochatbot}
            />
          ) : (
            <div className="flex flex-1 items-center justify-center px-3 text-center text-xs leading-relaxed text-[var(--color-muted-foreground)]">
              Related pages and offers from the assistant appear here.
            </div>
          )}
          {isFormOpen && formColumn === 'right' ? (
            <LeadFormInPanel onSubmit={onLeadFormSubmit} conf={conf} />
          ) : null}
          {isOrderStatusOpen && orderStatusColumn === 'right' ? (
            <OrderStatusInPanel conf={conf} onResult={onOrderStatusResult} />
          ) : null}
        </div>
      </div>
    </div>
  )
}
